import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { prettifyApiError } from "../../shared/api";
import { ErrorBanner } from "../../shared/ui/ErrorBanner";
import { CampoCerca, ChipFiltro } from "../../shared/ui/ChipFiltro";
import { PageHeader } from "../../shared/ui/PageHeader";
import {
  PER_PAGINA,
  fetchFattureFornitori,
  fetchRigheFattura,
  type ElencoFattureFornitori,
  type FatturaFornitore,
  type RigaFatturaFornitore,
} from "./fattureFornitoriApi";

const euro = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

const giorno = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const ANNO_CORRENTE = new Date().getFullYear();
const ANNI = [ANNO_CORRENTE, ANNO_CORRENTE - 1, ANNO_CORRENTE - 2, ANNO_CORRENTE - 3];

/** Le righe di una fattura, caricate quando la si apre. */
function Righe({ id }: { id: number }) {
  const [righe, setRighe] = useState<RigaFatturaFornitore[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  useEffect(() => {
    fetchRigheFattura(id)
      .then((d) => setRighe(d.righe))
      .catch((err: unknown) => setErrore(prettifyApiError(err)));
  }, [id]);
  if (errore) return <ErrorBanner message={errore} className="m-3" />;
  if (righe === null) return <p className="px-4 pb-3 text-[12.5px] text-ink-2">Carico le righe…</p>;
  return (
    <div className="overflow-x-auto px-4 pb-3">
      <table className="w-full min-w-[560px] text-left text-[12.5px]">
        <thead className="text-ink-2">
          <tr>
            <th className="py-1 pr-3 font-semibold">Codice</th>
            <th className="py-1 pr-3 font-semibold">Descrizione</th>
            <th className="py-1 pr-3 text-right font-semibold">Pezzi</th>
            <th className="py-1 pr-3 text-right font-semibold">Importo</th>
            <th className="py-1 font-semibold">Ordine</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {righe.map((r) => (
            <tr key={r.riga}>
              <td className="py-1.5 pr-3 font-mono text-[12px]">{r.codice || "—"}</td>
              <td className="py-1.5 pr-3">{r.descrizione}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums">{r.quantita}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums">{euro(r.importo)}</td>
              <td className="py-1.5">
                {r.ordine ? (
                  <Link
                    to={`/ordini/${encodeURIComponent(r.ordine)}`}
                    className="font-semibold text-ink hover:underline"
                  >
                    {r.ordine}
                  </Link>
                ) : (
                  <span className="text-ink-2">{r.rif_ordine || "—"}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Fattura({ f }: { f: FatturaFornitore }) {
  const [aperta, setAperta] = useState(false);
  const nota = f.tipo === "TD04";
  return (
    <li>
      <button
        type="button"
        aria-expanded={aperta}
        onClick={() => setAperta((v) => !v)}
        className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-paper-2 max-md:flex-wrap max-md:gap-x-3 max-md:gap-y-1"
      >
        <span className="w-[86px] shrink-0 text-[12.5px] tabular-nums text-ink-2">
          {giorno(f.data)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-ink">{f.fornitore}</span>
          <span className="block truncate text-[12.5px] text-ink-2">
            {f.tipo_nome} n. {f.numero}
            {f.ordini.length > 0 && ` · ordine ${f.ordini.slice(0, 2).join(", ")}`}
            {f.ordini.length > 2 && ` e altri ${f.ordini.length - 2}`}
          </span>
        </span>
        <span
          className={`shrink-0 text-right font-semibold tabular-nums ${nota ? "text-green" : "text-ink"}`}
        >
          {nota ? "− " : ""}
          {euro(f.imponibile)}
          <span className="block text-[11.5px] font-normal text-ink-2">senza IVA</span>
        </span>
      </button>
      {aperta && <Righe id={f.id} />}
    </li>
  );
}

export function FattureFornitoriPage() {
  const [dati, setDati] = useState<ElencoFattureFornitori | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [cerca, setCerca] = useState("");
  const [cercaAttiva, setCercaAttiva] = useState("");
  const [piva, setPiva] = useState("");
  const [anno, setAnno] = useState<number | null>(null);
  const [pagina, setPagina] = useState(0);

  // La ricerca parte quando ci si ferma di scrivere: una query per tasto no.
  useEffect(() => {
    const t = setTimeout(() => setCercaAttiva(cerca.trim()), 300);
    return () => clearTimeout(t);
  }, [cerca]);
  useEffect(() => setPagina(0), [cercaAttiva, piva, anno]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchFattureFornitori(
      {
        cerca: cercaAttiva,
        piva,
        dal: anno ? `${anno}-01-01` : undefined,
        al: anno ? `${anno}-12-31` : undefined,
        offset: pagina * PER_PAGINA,
      },
      ctrl.signal,
    )
      .then((d) => {
        setDati(d);
        setErrore(null);
      })
      .catch((err: unknown) => {
        if (!ctrl.signal.aborted) setErrore(prettifyApiError(err));
      });
    return () => ctrl.abort();
  }, [cercaAttiva, piva, anno, pagina]);

  const pagine = useMemo(() => Math.max(1, Math.ceil((dati?.totale ?? 0) / PER_PAGINA)), [dati]);

  return (
    <main className="flex-1 overflow-y-auto px-7 pt-[22px] pb-8 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Fatture fornitori"
        subtitle="Tutte le fatture di acquisto scaricate da Eureka. Cerca per fornitore, prodotto o numero d'ordine."
        meta={
          <Link to="/fatturazione" className="font-semibold text-ink hover:underline max-md:hidden">
            ← Fatturazione
          </Link>
        }
      />
      <ErrorBanner message={errore} className="mb-3" />

      <div className="mb-4 flex flex-wrap items-center gap-2 max-md:flex-nowrap max-md:overflow-x-auto max-md:pb-1">
        <ChipFiltro tono="annullato" attivo={anno === null} onClick={() => setAnno(null)}>
          Tutti gli anni
        </ChipFiltro>
        {ANNI.map((a) => (
          <ChipFiltro key={a} tono="annullato" attivo={anno === a} onClick={() => setAnno(a)}>
            {String(a)}
          </ChipFiltro>
        ))}
        <select
          aria-label="Fornitore"
          value={piva}
          onChange={(e) => setPiva(e.target.value)}
          className="h-[30px] max-w-[260px] rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-semibold text-ink outline-none focus-visible:ring-2 focus-visible:ring-ink-4 max-md:h-10 max-md:shrink-0 max-md:text-[14px]"
        >
          <option value="">Tutti i fornitori</option>
          {(dati?.fornitori ?? []).map((f) => (
            <option key={f.piva} value={f.piva}>
              {f.nome} ({f.fatture})
            </option>
          ))}
        </select>
        <CampoCerca
          value={cerca}
          onChange={setCerca}
          placeholder="Cerca fornitore, SKU, ordine, numero"
          ariaLabel="Cerca fra le fatture fornitori"
          className="w-[300px] max-md:hidden"
        />
      </div>
      <CampoCerca
        value={cerca}
        onChange={setCerca}
        placeholder="Cerca fornitore, SKU, ordine, numero"
        ariaLabel="Cerca fra le fatture fornitori"
        className="mb-4 hidden max-md:flex"
      />

      {dati === null && !errore && (
        <p className="text-[13px] text-ink-2" role="status">
          Carico le fatture…
        </p>
      )}
      {dati !== null && dati.fatture.length === 0 && (
        <div className="rounded-lg border border-dashed border-line-2 px-6 py-12 text-center">
          <p className="font-display text-[18px] font-bold text-ink">
            {dati.totale === 0 && !cercaAttiva && !piva && anno === null
              ? "Archivio ancora vuoto"
              : "Nessuna fattura con questi filtri"}
          </p>
          <p className="mt-1 text-[13px] text-ink-2">
            {dati.totale === 0 && !cercaAttiva && !piva && anno === null
              ? "Le fatture entrano da sole dalla cartella dello scarico Eureka."
              : "Prova un altro anno o togli la ricerca."}
          </p>
        </div>
      )}
      {dati !== null && dati.fatture.length > 0 && (
        <>
          <p className="mb-2 px-1 text-[12.5px] tabular-nums text-ink-2">
            {dati.totale.toLocaleString("it-IT")} {dati.totale === 1 ? "fattura" : "fatture"}
          </p>
          <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-card">
            {dati.fatture.map((f) => (
              <Fattura key={f.id} f={f} />
            ))}
          </ul>
          {pagine > 1 && (
            <nav aria-label="Pagine" className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={pagina === 0}
                onClick={() => setPagina((p) => p - 1)}
                className="h-[30px] rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-semibold text-ink disabled:opacity-50 max-md:h-10"
              >
                ← Più recenti
              </button>
              <span className="text-[12.5px] tabular-nums text-ink-2">
                Pagina {pagina + 1} di {pagine}
              </span>
              <button
                type="button"
                disabled={pagina + 1 >= pagine}
                onClick={() => setPagina((p) => p + 1)}
                className="h-[30px] rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-semibold text-ink disabled:opacity-50 max-md:h-10"
              >
                Più vecchie →
              </button>
            </nav>
          )}
        </>
      )}
    </main>
  );
}
